// ============================================================================
// 1. 임포트 및 전역 설정 (Imports & Config)
// ============================================================================
import React, { useState, useEffect, useMemo } from 'react'; // 🌟 [수정] useMemo 추가
import { 
  User, RefreshCw, X, Search, Plus, Save, Clock, AlertCircle, Trash2, ChevronDown // 🚨 [추가] ChevronDown 아이콘 추가
} from 'lucide-react';
import { crmDataService } from '../services/crmDataService'; 
import PermissionGate from './PermissionGate'; // 🚨 [추가] 자물쇠 부품 가져오기

// // 👇 새로 만든 공용 모달 부품 가져오기
import DealDetailModal from './DealDetailModal';  // 🚨 딜보드 히스토리 미사용으로 주석

// 노션(칸반 보드) 단계 설정
const NOTION_STAGES = ['미방문', '방문', '견적', '제안서', '사양서', '조달접수', '계약'] as const;
type NotionStage = typeof NOTION_STAGES[number];

// 각 단계별 라벨 및 색상 디자인 설정
const STAGE_CONFIG: Record<NotionStage, { label: string; color: string }> = {
  '미방문': { label: '미방문', color: 'bg-slate-500' },
  '방문': { label: '방문/미팅', color: 'bg-blue-600' },
  '견적': { label: '견적제출', color: 'bg-indigo-600' },
  '제안서': { label: '제안단계', color: 'bg-violet-600' },
  '사양서': { label: '사양확정', color: 'bg-purple-600' },
  '조달접수': { label: '조달/입찰', color: 'bg-orange-600' },
  '계약': { label: '계약완료', color: 'bg-emerald-600' },
};

interface DealBoardProps {
  deals: any[];         // 영업기회 목록
  contacts: any[];      // 고객 목록
  currentUser: any;     // 현재 로그인 사용자
  onRefresh?: () => void; // 부모에게 새로고침 알림용
  globalRefreshKey?: number; // 🚨 [신규] 실시간 신호탄
  currentTime?: string;
}

const DealBoard: React.FC<DealBoardProps> = ({ deals = [], contacts = [], currentUser, onRefresh, globalRefreshKey = 0, currentTime }) => {
  // ============================================================================
  // 2. 상태 관리 (State Management)
  // ============================================================================
  const [isSyncing, setIsSyncing] = useState(false);
  const [localDeals, setLocalDeals] = useState<any[]>([]); 
  
  // // 🌟 [통합 모달 상태] 모달 관리가 아주 심플해졌습니다!
  const [selectedDealDetail, setSelectedDealDetail] = useState<any>(null); // 🚨 모달 미사용으로 주석 처리

  // 🚀 [신규] 빠른 추가 (Quick Add) 모달 상태 (대표님 코드 유지)
  const [isAddDealOpen, setIsAddDealOpen] = useState(false);
  const [newDealForm, setNewDealForm] = useState({
    company_name: '', title: '', assignee: '미정', month: new Date().getMonth() + 1, planned_product: 0, planned_item: 0, source: '자체 발굴'
  });

  // 🔍 [추가] 거래처 검색 상태
  const [searchTerm, setSearchTerm] = useState('');

  // 🚨 [추가] 담당자 필터 상태 및 DB 유저 목록 상태
  const [selectedAssignee, setSelectedAssignee] = useState('전체');
  const [dbUsers, setDbUsers] = useState<any[]>([]);

  // ============================================================================
  // 3. 데이터 로딩 및 헬퍼 함수
  // ============================================================================
  
  const loadBoardData = async () => {
    setIsSyncing(true);
    try {
      // 🚨 [핵심 수정] 모달 미사용으로 메모/활동(allActivities)을 불러오지 않고 가볍게 영업기회만 가져옵니다!
      const forecastList = await crmDataService.getDeals();
      setLocalDeals(forecastList || []);

      // 🚨 [추가] 드롭다운 필터링을 위해 유저 목록도 같이 가져옵니다.
      const users = await crmDataService.getAllUsers();
      setDbUsers(users || []);

      /* 🚨 기존 무거운 데이터 로딩 로직 주석 처리
      const [forecastList, allActivities] = await Promise.all([
        crmDataService.getDeals(), 
        crmDataService.getAllDealActivities()
      ]);
      
      const combined = (forecastList || []).map((deal: any) => {
        const act = allActivities[deal.id] || {};
        return {
          ...deal,
          last_meet: act.last_meet || '-',
          next_plan: act.next_plan || '-',
          memo: act.memo || '',
          ai_insight: act.ai_insight || '',
          deep_insight: act.deep_insight || '',
          deep_insight_updated_at: act.deep_insight_updated_at || ''
        };
      });
      
      setLocalDeals(combined);
      */
    } catch (error) {
      console.error("딜보드 데이터 로딩 실패:", error);
    } finally {
      setIsSyncing(false);
    }
  };

  useEffect(() => { loadBoardData(); }, [globalRefreshKey]); // 🚨 [핵심 수정] globalRefreshKey가 변경될 때마다 데이터 새로고침

  const formatKRW = (val: number) => new Intl.NumberFormat('ko-KR').format(val || 0);
  
  const currentYear = new Date().getFullYear().toString(); 
  
  // 🚨 [추가] 상단 필터용 드롭다운 옵션 ('전체' 포함)
  const salesTeamOptions = useMemo(() => {
    if (!dbUsers || dbUsers.length === 0) return ['전체'];
    const domesticUsers = dbUsers.filter((u: any) => {
      const tName = u.team_name || u.team || '';
      return tName.includes('국내') || tName.includes('영업');
    }).map((u: any) => u.name);
    return ['전체', ...Array.from(new Set(domesticUsers))];
  }, [dbUsers]);

  // 🚨 [추가] 신규 등록 모달용 드롭다운 옵션 ('미정', '기타' 포함, '전체' 제외)
  const assignOptions = useMemo(() => {
    if (!dbUsers || dbUsers.length === 0) return ['미정', '기타'];
    const domesticUsers = dbUsers.filter((u: any) => {
      const tName = u.team_name || u.team || '';
      return tName.includes('국내') || tName.includes('영업');
    }).map((u: any) => u.name);
    return ['미정', ...Array.from(new Set(domesticUsers)), '기타'];
  }, [dbUsers]);

  // 🔍 [수정] 검색 필터가 적용된 딜 리스트 계산 (담당자 검색 조건 추가!)
  const filteredDeals = useMemo(() => {
    return localDeals.filter((deal: any) => {
      const isCurrentYear = (deal['매출예정일'] || '').startsWith(currentYear) || (deal['예정월'] || deal['month']) > 0;
      
      // 🚨 [핵심 수정] 텍스트 검색 조건에 '담당자'를 추가했습니다!
      const searchLower = searchTerm.toLowerCase();
      const matchesSearch = 
        (deal['거래처'] || '').toLowerCase().includes(searchLower) || 
        (deal['건명'] || '').toLowerCase().includes(searchLower) ||
        (deal['담당자'] || '').toLowerCase().includes(searchLower); // 👈 담당자 이름으로도 텍스트 검색 가능!

      // 🚨 [추가] 드롭다운 담당자 필터 일치 여부
      const matchesAssignee = selectedAssignee === '전체' || (deal['담당자'] || '미정') === selectedAssignee;

      return isCurrentYear && matchesSearch && matchesAssignee; // 👈 적용
    });
  }, [localDeals, searchTerm, currentYear, selectedAssignee]); // 👈 의존성에 selectedAssignee 추가

  const getDealsByStage = (stage: string) => {
    return filteredDeals.filter((deal: any) => {
      const dealStage = deal['영업리드'];
      if (!dealStage || dealStage === '-' || !NOTION_STAGES.includes(dealStage as any)) return stage === '미방문';
      return dealStage === stage;
    });
  };

  // ============================================================================
  // 4. 이벤트 핸들러
  // ============================================================================

  const handleDragStart = (e: React.DragEvent, dealId: string) => { e.dataTransfer.setData("dealId", dealId); };
  const handleDragOver = (e: React.DragEvent) => { e.preventDefault(); };

  // 이탈 처리 드롭 핸들러
  const handleDropToLost = async (e: React.DragEvent) => {
    e.preventDefault();
    const dealId = e.dataTransfer.getData("dealId");
    if (!dealId) return;

    // 드래그 중인 딜의 정보를 찾습니다.
    const targetDeal = localDeals.find(d => d.id === dealId);
    const clientName = targetDeal ? targetDeal['거래처'] : '이 거래처';

    if (window.confirm(`[${clientName}] 건을 이탈 처리하시겠습니까?\n매출예정표와 딜보드에서 제외됩니다.`)) {
      const reason = prompt("이탈 사유를 입력해주세요 (선택):") || "딜보드 드래그 이탈";
      
      try {
        // 백엔드 호출: 구분을 '이탈'로 변경
        const success = await crmDataService.updateForecastItem(
          dealId, 
          { 구분: '이탈' }, 
          `[이탈처리] ${reason}`, 
          currentUser?.name || "담당자"
        );

        if (success) {
          alert("이탈 거래처로 이동되었습니다.");
          loadBoardData(); // 딜보드 새로고침
          if (onRefresh) onRefresh(); // 부모(App.tsx)에게 알려서 매출예정표 등 동기화
        }
      } catch (err) {
        console.error("이탈 처리 실패:", err);
        alert("처리에 실패했습니다.");
      }
    }
  };
  
  const handleDrop = async (e: React.DragEvent, newStage: string) => {
    e.preventDefault();
    const dealId = e.dataTransfer.getData("dealId");
    if (!dealId) return;

    setLocalDeals(prev => prev.map(deal => deal.id === dealId ? { ...deal, 영업리드: newStage } : deal));
    try {
      await crmDataService.updateForecastItem(dealId, { 영업리드: newStage }, "딜보드 이동", currentUser?.name || "담당자");
      // 🚨 [유지] 아까 고치셨던 content 포함 코드 그대로 살렸습니다! (422 에러 방지)
      await crmDataService.saveDealActivity({ deal_id: dealId, activity_type: newStage, content: `단계를 ${newStage}로 변경` });
      loadBoardData();
      if (onRefresh) onRefresh();
    } catch (err) { console.error("이동 저장 실패", err); loadBoardData(); }
  };

  // 🚨 [삭제] 모달 열기용 클릭 함수 주석 처리
  const handleCardClick = (deal: any) => {
    setSelectedDealDetail(deal);
  };

  const handleAddNewDeal = async () => {
    if (!newDealForm.company_name) { alert("거래처명을 입력해 주세요!"); return; }
    try {
      const monthStr = String(newDealForm.month).padStart(2, '0');
      const fakeDate = `${currentYear}-${monthStr}-01`; 
      const finalTitle = newDealForm.title.trim() || "-";

      const newDealData = {
        project_name: finalTitle, client_name: newDealForm.company_name, 
        sales_amount: Number(newDealForm.planned_product) + Number(newDealForm.planned_item),
        sales_date: fakeDate, sales_amount_prod: Number(newDealForm.planned_product), sales_amount_item: Number(newDealForm.planned_item),
        assignee: newDealForm.assignee || "미정", team: "미배정", status: "예정",
        source: newDealForm.source
      };

      const success = await crmDataService.addForecastItem(newDealData);
      if (success) {
        setIsAddDealOpen(false);
        setNewDealForm({ company_name: '', title: '', assignee: '미정', month: new Date().getMonth() + 1, planned_product: 0, planned_item: 0, source: '자체 발굴' });
        loadBoardData(); 
        if (onRefresh) onRefresh();
      } else { alert("저장에 실패했습니다."); }
    } catch (error) { alert("저장에 실패했습니다."); }
  };

  // ============================================================================
  // 5. 화면 렌더링
  // ============================================================================
  return (
    <div className="flex flex-col h-screen bg-[#f1f5f9] p-6 overflow-hidden relative">
      
      {/* 🚀 상단 컨트롤 영역 (이탈 처리 존 통합) */}
      <div className="flex items-center justify-between mb-6 bg-white p-3 rounded-2xl border border-slate-300 shadow-sm">
        <div className="flex items-center gap-4">
          {/* 현황 표시 */}
          <div className="flex items-center gap-2 border-r border-slate-200 pr-5">
            <span className="text-slate-900 text-sm font-[1000]">{currentYear}년 현황:</span>
            <span className="text-indigo-600 text-base font-[1000] tracking-tighter">{filteredDeals.length}건</span>
          </div>

          {/* 신규 영업 등록 버튼 - 🚨 PermissionGate 채우기 */}
          <PermissionGate user={currentUser} action="pipeline_edit">
            <button onClick={() => setIsAddDealOpen(true)} className="flex items-center gap-2 bg-slate-900 px-4 py-2 rounded-xl text-xs font-[1000] text-white hover:bg-slate-800 transition-all shadow-md active:scale-95">
              <Plus size={16} strokeWidth={3} /> 신규 등록
            </button>
          </PermissionGate>

          {/* 🔍 검색 필터 */}
          <div className="flex items-center gap-2 bg-slate-50 border-2 border-slate-400 px-3 py-2 rounded-xl focus-within:border-indigo-600 focus-within:bg-white transition-all shadow-inner">
            <Search size={16} className="text-indigo-600" strokeWidth={3} />
            <input 
              type="text" 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="거래처/건명/담당자 검색..." /* 🚨 안내 문구 수정 */
              className="bg-transparent text-xs font-[1000] outline-none w-48 text-slate-800 placeholder:text-slate-400"
            />
          </div>

          {/* 🚨 [추가] 담당자 필터 드롭다운 (국내팀만) */}
          <div className="relative animate-fade-in-right ml-1">
            <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            <select 
              value={selectedAssignee}
              onChange={(e) => setSelectedAssignee(e.target.value)}
              className="appearance-none pl-4 pr-9 py-2 text-xs font-[1000] bg-indigo-50 text-indigo-700 rounded-xl border-2 border-indigo-200 outline-none focus:ring-2 focus:ring-indigo-400 cursor-pointer shadow-sm hover:bg-indigo-100 transition-colors"
            >
              {salesTeamOptions.map(opt => (
                <option key={opt} value={opt}>{opt === '전체' ? '국내팀 전체 보기' : opt}</option>
              ))}
            </select>
          </div>

          {/*이탈 처리 드롭존 (검색 필터 옆 배치) - 🚨 PermissionGate 채우기 */}
          <PermissionGate user={currentUser} action="pipeline_edit">
            <div 
              onDragOver={handleDragOver}
              onDrop={handleDropToLost}
              className="flex items-center gap-2 px-6 py-2 bg-rose-50 border-2 border-dashed border-rose-300 rounded-xl text-rose-600 transition-all hover:bg-rose-100 hover:border-rose-500 group cursor-pointer"
            >
              <AlertCircle size={16} className="group-hover:animate-bounce" strokeWidth={3} />
              <span className="text-xs font-[1000] uppercase tracking-tight">여기로 드래그하여 이탈 처리</span>
            </div>
          </PermissionGate>
        </div>

        {/* 새로고침 아이콘 */}
        <button onClick={() => loadBoardData()} className="p-2.5 bg-white border-2 border-slate-200 rounded-xl hover:bg-slate-50 transition-all text-slate-500">
          <RefreshCw size={18} className={isSyncing ? "animate-spin" : ""} />
        </button>
      </div>

      <div className="flex-1 overflow-x-auto custom-scrollbar">
        <div className="flex gap-4 h-full pb-6" style={{ minWidth: 'min-content' }}>
          {NOTION_STAGES.map((stage) => {
            const stageDeals = getDealsByStage(stage);
            const stageTotalAmount = stageDeals.reduce((sum, deal) => sum + (Number(deal['소계']) || 0), 0);

            return (
              <div key={stage} className="w-80 flex flex-col h-full bg-slate-200/40 rounded-[28px] border border-slate-300 p-3" onDragOver={handleDragOver} onDrop={(e) => handleDrop(e, stage)}>
                
                {/* 스테이지 제목 및 금액 헤더 */}
                <div className="flex flex-col gap-2 mb-4 px-1">
                  <div className="flex justify-between items-center">
                    <div className="flex items-center gap-2 font-[1000] text-slate-800 text-[13px] uppercase tracking-tight">
                      <div className={`w-2.5 h-2.5 rounded-full ${STAGE_CONFIG[stage].color} shadow-sm`}></div>
                      {STAGE_CONFIG[stage].label}
                    </div>
                    <span className="text-slate-600 text-[10px] font-[1000] bg-white border border-slate-300 px-2 py-0.5 rounded-full shadow-sm">{stageDeals.length}건</span>
                  </div>
                  
                  <div className="bg-white border-2 border-slate-300 rounded-xl px-3 py-2 flex justify-between items-center shadow-sm">
                    <span className="text-[10px] text-slate-500 font-bold uppercase">매출 예상액</span>
                    <span className="font-mono text-indigo-600 text-sm font-[1000]">₩ {formatKRW(stageTotalAmount)}</span>
                  </div>
                </div>

                <div className="flex-1 overflow-y-auto space-y-3 pr-1 custom-scrollbar">
                  {stageDeals.map((deal: any) => (
                    /* 🚨 [수정] 드래그 기능도 PermissionGate로 제어하여 권한 유저만 카드를 옮길 수 있게 합니다 */
                    <PermissionGate 
                      key={deal.id} 
                      user={currentUser} 
                      action="pipeline_edit"
                      /* 💡 권한이 없으면 PermissionGate가 children을 안 그리므로, 
                         그냥 일반 유저도 카드는 봐야 하니까 꼼수로 
                         자물쇠를 카드 내부에 안 치고, 껍데기는 유지하되
                         드래그 이벤트(onDragStart)에만 락을 거는 방식으로 구현할 수도 있습니다.
                         하지만 대표님 요청대로 '데이터 수정' 길목을 막기 위해 
                         가장 확실한 방법인 '이동 기능 자체를 락' 하겠습니다.
                      */
                    >
                      <div draggable onDragStart={(e) => handleDragStart(e, deal.id)}
                        // 🚨 [수정] 모달 미사용으로 클릭 이벤트 주석 처리 및 커서(cursor-pointer) 스타일 제거
                        onClick={() => handleCardClick(deal)} 
                        className="bg-white rounded-2xl shadow-md border-2 border-slate-200 p-3 hover:border-indigo-600 transition-all group relative overflow-hidden flex flex-col gap-2.5 cursor-grab active:cursor-grabbing"
                      >
                        {/* 🚀 카드 상단: 거래처 밑에 건명(프로젝트명) 배치 */}
                        <div className="flex justify-between items-start gap-2">
                          <div className="flex-1 min-w-0">
                            <h4 className="text-[13px] font-[1000] text-slate-900 leading-tight truncate">
                              {deal['거래처']}
                            </h4>
                            {deal['건명'] && (
                              <p className="text-[11px] text-slate-400 font-bold truncate mt-1">
                                [{deal['건명']}]
                              </p>
                            )}
                          </div>
                          <span className="text-[10px] font-[1000] text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded border border-slate-200 shrink-0">
                            {deal['담당자'] || '미정'}
                          </span>
                        </div>

                        {/* 카드 하단: 소계 강조 */}
                        <div className="flex justify-between items-center bg-slate-50 px-2.5 py-1.5 rounded-lg border border-slate-100">
                          <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none">소계</span>
                          <div className="text-[14px] font-mono font-[1000] text-slate-900 leading-none">
                            <span className="text-[10px] text-slate-400 font-normal mr-0.5">₩</span>{formatKRW(deal['소계'])}
                          </div>
                        </div>
                      </div>
                    </PermissionGate>
                  ))}
                  
                  {/* 💡 꼼수 방지: 권한이 없는 일반 유저도 카드는 봐야 하므로 
                      PermissionGate 바깥에 "드래그 안 되는 눈팅용 카드"를 별도로 띄워줍니다. 
                      (권한 체크 결과가 false일 때만 보여줌) */}
                  {stageDeals.map((deal: any) => (
                    currentUser?.role !== 'admin' && !currentUser?.permissions?.edit_pipeline && (
                      <div key={`view-${deal.id}`}
                        className="bg-white rounded-2xl shadow-md border-2 border-slate-200 p-3 transition-all group relative overflow-hidden flex flex-col gap-2.5 opacity-90"
                      >
                        <div className="flex justify-between items-start gap-2">
                          <div className="flex-1 min-w-0">
                            <h4 className="text-[13px] font-[1000] text-slate-900 leading-tight truncate">{deal['거래처']}</h4>
                            {deal['건명'] && <p className="text-[11px] text-slate-400 font-bold truncate mt-1">[{deal['건명']}]</p>}
                          </div>
                          <span className="text-[10px] font-[1000] text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded border border-slate-200 shrink-0">{deal['담당자'] || '미정'}</span>
                        </div>
                        <div className="flex justify-between items-center bg-slate-50 px-2.5 py-1.5 rounded-lg border border-slate-100">
                          <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none">소계</span>
                          <div className="text-[14px] font-mono font-[1000] text-slate-900 leading-none">
                            <span className="text-[10px] text-slate-400 font-normal mr-0.5">₩</span>{formatKRW(deal['소계'])}
                          </div>
                        </div>
                      </div>
                    )
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* 🌟 5-3. 상세 모달 (로직 유지) */}
      {/* 🚨 모달 미사용으로 전체 주석 처리 */}
      {selectedDealDetail && (
        <DealDetailModal 
          deal={selectedDealDetail} 
          currentUser={currentUser} 
          onClose={() => setSelectedDealDetail(null)}
          globalRefreshKey={globalRefreshKey}
          onRefresh={() => {
            loadBoardData();
            if (onRefresh) onRefresh();
          }} 
        />
      )}
      

      {/* 5-4. 신규 등록 모달 (로직 유지) */}
      {isAddDealOpen && (
        <div className="fixed inset-0 bg-slate-900/70 backdrop-blur-md z-[300] flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md overflow-hidden animate-in zoom-in duration-200 border border-slate-200">
            <div className="p-6 bg-slate-900 text-white flex justify-between items-center">
              <h3 className="text-lg font-black flex items-center gap-2">
                <Plus size={20} className="text-indigo-400" strokeWidth={3} /> 신규 영업 등록
              </h3>
              <button onClick={() => setIsAddDealOpen(false)} className="hover:rotate-90 transition-transform"><X size={20}/></button>
            </div>
            
            <div className="p-6 space-y-4 bg-slate-50">
              <div className="space-y-1">
                <label className="text-xs font-black text-slate-500">거래처명 <span className="text-red-500">*</span></label>
                <input type="text" placeholder="예: 삼성전자" value={newDealForm.company_name} onChange={e => setNewDealForm({...newDealForm, company_name: e.target.value})} className="w-full p-3 rounded-xl border border-slate-300 text-sm font-bold focus:border-indigo-600 outline-none" />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-black text-slate-500">건명 (프로젝트명)</label>
                <input type="text" placeholder="미입력 시 '-' 표시" value={newDealForm.title} onChange={e => setNewDealForm({...newDealForm, title: e.target.value})} className="w-full p-3 rounded-xl border border-slate-300 text-sm font-bold focus:border-indigo-600 outline-none" />
              </div>
              
              {/* 🚨 [추가] 모달 내 담당자 선택 드롭다운 */}
              <div className="space-y-1">
                <label className="text-xs font-black text-slate-500">담당자</label>
                <div className="relative">
                  <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                  <select 
                    value={newDealForm.assignee} 
                    onChange={e => setNewDealForm({...newDealForm, assignee: e.target.value})} 
                    className="w-full p-3 pr-9 rounded-xl border border-slate-300 text-sm font-bold focus:border-indigo-600 outline-none appearance-none bg-white cursor-pointer"
                  >
                    {assignOptions.map(rep => <option key={rep} value={rep}>{rep}</option>)}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3 pt-2">
                <div className="space-y-1">
                  <label className="text-xs font-black text-indigo-600">제품금액(₩)</label>
                  <input type="number" value={newDealForm.planned_product || ''} onChange={e => setNewDealForm({...newDealForm, planned_product: Number(e.target.value)})} className="w-full p-3 rounded-xl border border-indigo-200 bg-white text-sm font-mono font-[1000] focus:border-indigo-600 outline-none" />
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-black text-indigo-600">상품금액(₩)</label>
                  <input type="number" value={newDealForm.planned_item || ''} onChange={e => setNewDealForm({...newDealForm, planned_item: Number(e.target.value)})} className="w-full p-3 rounded-xl border border-indigo-200 bg-white text-sm font-mono font-[1000] focus:border-indigo-600 outline-none" />
                </div>
              </div>

              <div className="bg-slate-900 rounded-xl p-4 mt-2 shadow-inner border border-slate-800">
                <span className="text-xs font-black text-slate-400">총 예상 매출액 (소계)</span>
                <p className="text-xl font-mono font-[1000] text-emerald-400 mt-1 leading-none">
                  ₩{(Number(newDealForm.planned_product) + Number(newDealForm.planned_item)).toLocaleString()}
                </p>
              </div>
            </div>

            <div className="p-4 bg-white flex gap-2">
              <button onClick={() => setIsAddDealOpen(false)} className="flex-1 py-3 rounded-xl font-black text-slate-500 bg-slate-100 hover:bg-slate-200">취소</button>
              <button onClick={handleAddNewDeal} className="flex-[2] py-3 rounded-xl font-black text-white bg-indigo-600 hover:bg-indigo-700 shadow-md">딜보드에 생성하기</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DealBoard;